import type {
  CommendaAiConversationAccessView,
  CommendaAiConversationVisibility,
} from '@commenda/api/commenda-ai/types';
import {
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useState } from 'react';
import { ConversationAccessHelper } from './conversation-access-helper';

interface ConversationVisibilityControlProps {
  access: CommendaAiConversationAccessView;
  /** Shown in the "Everyone in …" option. */
  workspaceName?: string;
  /** Absent when the viewer may not change who can see the chat: the pill then renders read-only. */
  onChange?: (visibility: CommendaAiConversationVisibility) => void;
  /** Drops the label, leaving the icon alone; read-only only. For the narrow
   * panel header, where the icon sits in the row of icon buttons. */
  iconOnly?: boolean;
}

/** "Private" / "Shared" pill in the chat header. Owners get a popover to choose who can access the
 * chat; everyone else sees the current state, who shared it, and why they can't change it. */
export function ConversationVisibilityControl({
  access,
  workspaceName = 'your workspace',
  onChange,
  iconOnly = false,
}: ConversationVisibilityControlProps) {
  const [isAccessPopoverOpen, setIsAccessPopoverOpen] = useState(false);
  const { visibility, readOnlyReason } = access;
  const CurrentIcon = ConversationAccessHelper.getOption(visibility).icon;
  const label = ConversationAccessHelper.getPillLabel(access);

  if (!onChange) {
    const pill = (
      <span
        className={cn(
          'inline-flex shrink-0 cursor-default items-center text-text-tertiary',
          iconOnly
            ? 'size-6 justify-center'
            : 'h-7 gap-xs px-md txt-small-regular',
          readOnlyReason && 'cursor-help'
        )}
        aria-label={readOnlyReason ? `${label}. ${readOnlyReason}` : label}
      >
        <CurrentIcon
          className={iconOnly ? 'size-4' : 'size-3.5'}
          aria-hidden="true"
        />
        {iconOnly ? null : label}
      </span>
    );
    // Icon alone carries no text, so it always gets a tooltip naming the current access.
    const tooltip = iconOnly
      ? [label, readOnlyReason].filter(Boolean).join(' · ')
      : readOnlyReason;
    if (!tooltip) return pill;
    return (
      <Tooltip content={tooltip} asChild>
        {pill}
      </Tooltip>
    );
  }

  return (
    <Popover open={isAccessPopoverOpen} onOpenChange={setIsAccessPopoverOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change who can access this chat"
          className={cn(
            'inline-flex h-7 shrink-0 cursor-pointer items-center gap-xs rounded-md border-none bg-transparent px-md txt-small-medium text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary',
            isAccessPopoverOpen && 'bg-bg-tertiary text-text-primary'
          )}
        >
          <CurrentIcon className="size-3.5" aria-hidden="true" />
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[400px] rounded-xl border-[0.5px] border-border-primary p-none shadow-md"
      >
        <div className="flex flex-col py-sm">
          <p className="px-lg py-md txt-extra-small-medium text-text-secondary">
            Who can access this chat?
          </p>
          <div role="radiogroup" className="flex flex-col px-sm">
            {ConversationAccessHelper.VISIBILITY_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = option.value === visibility;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => {
                    if (!isSelected) onChange(option.value);
                    setIsAccessPopoverOpen(false);
                  }}
                  className="flex w-full items-start gap-sm rounded-sm py-md pl-md pr-lg text-left transition-colors hover:bg-bg-secondary"
                >
                  <span className="flex h-5 w-4 shrink-0 items-center justify-center">
                    <Icon
                      className="size-4 text-fg-secondary"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate txt-small-medium text-text-primary">
                      {option.getTitle(workspaceName)}
                    </span>
                    <span className="txt-extra-small-regular text-text-tertiary">
                      {option.description}
                    </span>
                  </span>
                  <span className="flex h-5 w-4 shrink-0 items-center justify-center">
                    <Icons.Check
                      className={cn(
                        'size-4 text-fg-primary',
                        !isSelected && 'invisible'
                      )}
                      aria-hidden="true"
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-md px-lg pb-lg pt-md">
          <div className="border-t-[0.5px] border-border-secondary" />
          <p className="txt-extra-small-regular text-text-tertiary">
            People with access can see the entire conversation and any files
            attached to it.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
